import notify from "devextreme/ui/notify";
import io, { Socket } from "socket.io-client";
import "../css/hermes.css";
import ControleSessao from "../componentes-padrao/ControleSessao";
import { criarPainelNotificacoes } from "./PainelNotificacoes";

/**
 * @typedef Notificacao
 * @property id {number}
 * @property to {number}
 * @property received {boolean}
 * @property message {string}
 * @property messageType {string}
 * @property displayTime {number}
 * @property read {boolean}
 * @property recipient {string}
 * @property createdAt {Date}
 */

/**
 * Notificações em tempo real do Hermes.
 *
 * A apresentação saiu para PainelNotificacoes.tsx no redesign (JOR4-7365);
 * aqui ficou o que é comportamento: socket, histórico e leitura. A lista passa a
 * viver nesta classe, e não mais dentro do dataSource de um dxList — o
 * componente só recebe o que desenhar.
 */
export default class HermesNotifier {
    /** @type {Notificacao[]} */
    notificacoes = [];

    /**
     * @param {(HTMLElement | JQuery)} element
     */
    constructor(element) {
        const alvo = element instanceof HTMLElement ? element : $(element).get(0);

        this.painel = criarPainelNotificacoes(alvo);
        this.painel.atualizar({
            aoMarcarComoLida: (id) => this.marcarComoLida(id),
            aoMarcarTodasComoLidas: () => this.marcarTodasComoLidas(),
        });

        const disableNotifications = process.env.DISABLE_NOTIFICATIONS;

        if (disableNotifications === true || disableNotifications === "true") {
            return;
        }

        this.configureNotifications();
    }

    /**
     * @return {Socket}
     */
    get socket() {
        return this._socket;
    }

    /**
     * @param {Socket} socket
     */
    set socket(socket) {
        this._socket = socket;
    }

    /** Empurra a lista atual para o painel. */
    sincronizarPainel() {
        this.painel.atualizar({ notificacoes: [...this.notificacoes] });
    }

    /** @param {number} id */
    marcarComoLida(id) {
        const notificacao = this.notificacoes.find((item) => item.id === id);

        if (!notificacao || notificacao.read) return;

        notificacao.read = true;
        this.socket?.emit("read_message", id);
        this.sincronizarPainel();
    }

    marcarTodasComoLidas() {
        const naoLidas = this.notificacoes.filter((item) => !item.read);

        if (!naoLidas.length) return;

        naoLidas.forEach((item) => {
            item.read = true;
            this.socket?.emit("read_message", item.id);
        });

        this.sincronizarPainel();
    }

    /**
     * Encerra o socket e desmonta o painel. Sem isto, trocar de empresa deixava
     * a conexão anterior viva recebendo `notify` para um painel órfão.
     */
    destruir() {
        if (this.socket) {
            this.socket.off();
            this.socket.disconnect();
            this.socket = null;
        }

        this.painel.destruir();
    }

    configureNotifications = () => {
        const socket = this.connectToSocketIo();

        const sessionID = localStorage.getItem("sessionID");

        if (sessionID) socket.auth = { sessionID };

        socket.connect();

        this.socket = socket;

        socket
            .on("connect", () => {
                socket
                    .on("notify", (msg) => {
                        new notify(
                            {
                                position: { at: "top", my: "top", offset: "0 60" },
                                maxWidth: 400,
                                closeOnClick: true,
                                contentTemplate: function (contentElement) {
                                    const box = $("<p />").html(msg.message);
                                    $(contentElement).append(box);
                                },
                            },
                            msg.messageType,
                            msg.displayTime ?? 3000,
                        );

                        this.notificacoes.unshift(msg);
                        this.sincronizarPainel();
                    })
                    .on("message_history", (msg) => {
                        this.notificacoes = Array.isArray(msg) ? msg : [];
                        this.sincronizarPainel();
                    })
                    .on("session", ({ sessionID, userID }) => {
                        socket.auth = { sessionID };
                        localStorage.setItem("sessionID", sessionID);
                        socket.userID = userID;
                    });
            })
            .on("unauthorized", (msg) => {
                console.error(`unauthorized: ${JSON.stringify(msg.data)}`);
                throw new Error(msg.data.type);
            })
            .on("disconnect", (_) => {
                socket.off("notify");
                socket.off("message_history");
                socket.off("session");
            });
    };

    /**
     * @return {Socket}
     */
    connectToSocketIo = () => {
        const accessToken = ControleSessao.getToken();

        return io(window.location.origin, {
            path: "/hermes/socket.io",
            autoConnect: false,
            withCredentials: true,
            extraHeaders: {
                "x-auth-token": accessToken,
            },
            transportOptions: {
                polling: {
                    extraHeaders: {
                        "x-auth-token": accessToken,
                    },
                },
            },
        });
    };
}
